/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import React from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	trigger: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		color: token('color.text.subtle'),
		columnGap: token('space.050'),
		display: 'inline-flex',
		font: token('font.body.small'),
		marginInlineEnd: token('space.0'),
		paddingBlockEnd: token('space.050'),
		paddingBlockStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		paddingInlineStart: token('space.050'),
	},
	interactive: {
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	dot: {
		'&::before': {
			alignSelf: 'center',
			content: '"•"',
			display: 'inline-block',
			paddingInlineEnd: token('space.050'),
			textDecoration: 'none',
		},
	},
});

export type BylineAppTriggerVisualProps = Omit<ComponentPropsWithoutRef<'span'>, 'children'> & {
	children: ReactNode;
	isInteractive?: boolean;
	showDot?: boolean;
};

/**
 * Shared visual contract for Connect, Forge, and inert SSR byline app triggers.
 * Callers retain the anchor, button, or inert semantics required by their runtime behavior.
 */
export const BylineAppTriggerVisual = ({
	children,
	isInteractive = false,
	showDot = false,
	...htmlAttributes
}: BylineAppTriggerVisualProps): React.JSX.Element => (
	<span
		{...htmlAttributes}
		css={[styles.trigger, isInteractive && styles.interactive, showDot && styles.dot]}
	>
		{children}
	</span>
);
